import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { doc, updateDoc } from "firebase/firestore";
import { CircularProgress } from "@mui/material";
import { db } from "./firebase";

const ImageEditDialog = ({ open, onClose, image, theme, styles }) => {
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (open && image) {
      setTags(image.tags || []);
    }
  }, [open, image]);

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const imageRef = doc(db, "images", image.id);
      await updateDoc(imageRef, {
        tags: tags,
      });
      onClose();
    } catch (error) {
      console.error("Error updating image:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const commonTags = [
    "Människor",
    "Djur",
    "Jul",
    "Påsk",
    "Alla hjärtans dag",
    "Mat",
    "Sport",
    "Miljö",
    "Musik",
    "Mytologi",
    "Sagor",
    "Dixit",
    "Fylla i bilder",
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: theme.palette.mode === "dark" ? "#192734" : "#fff",
          width: "100%",
          maxWidth: "800px",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "700" }}>
          Redigera bild
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Image Preview */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <img
            src={image?.imageUrl}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "300px",
              objectFit: "contain",
              borderRadius: "10px",
            }}
          />
        </Box>

        {/* Common Tags */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Vanliga taggar:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {commonTags.map((tag) => (
            <Button
              key={tag}
              variant={
                tag === "1-3" ||
                tag === "4-6" ||
                tag === "7-9" ||
                tag === "Gymnasiet"
                  ? "success"
                  : tag === "Nationella provet"
                    ? "purp"
                    : "print"
              }
              startIcon={
                tags.includes(tag) ? (
                  <CheckCircleIcon sx={{ color: "green" }} />
                ) : null
              }
              onClick={() => {
                if (tags.includes(tag)) {
                  setTags(tags.filter((t) => t !== tag));
                } else {
                  setTags([...tags, tag]);
                }
              }}
              sx={{
                ...(styles?.tightButton || {}),
              }}
            >
              {tag}
            </Button>
          ))}
        </Box>

        {/* Custom Tags */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Lägg till egna taggar:
        </Typography>
        <TextField
          fullWidth
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && tagInput.trim()) {
              setTags([...new Set([...tags, tagInput.trim()])]);
              setTagInput("");
            }
          }}
          placeholder="Skriv och tryck enter för att lägga till tagg"
          variant="outlined"
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Valda taggar:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => setTags(tags.filter((t) => t !== tag))}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Button
          variant={theme.palette.mode === "light" ? "purp" : "contained"}
          onClick={handleSave}
          startIcon={isSaving ? "" : null}
          disabled={isSaving}
        >
          {isSaving ? "Sparar..." : "Spara ändringar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageEditDialog;
